import { useState, useCallback, useEffect } from "react";
import { Outlet } from "react-router-dom";

import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { RouteErrorBoundary } from "../components/ErrorBoundary";

/**
 * Template §10. The chrome renders ONCE and persists across navigation, so switching pages never
 * re-mounts the sidebar or refires its queries.
 */
export default function AdminLayout() {
  const [open, setOpen] = useState(false);

  // The drawer closes in the nav-item click handler (`onNavigate` below), not in an effect on the
  // location. Synchronising state to a route change with `useEffect(() => setOpen(false), [path])`
  // costs an extra render on every navigation and is what React's own guidance calls out — the event
  // that closes the drawer is the click, so that is where it is handled.
  const toggle = useCallback(() => setOpen((o) => !o), []);

  // ── PART 4 §4.22 · ESCAPE CLOSES THE DRAWER ─────────────────────────────────────────────────
  // The drawer is a dismissible overlay: it covers the page, dims it with a backdrop, and traps the
  // reader's attention. Escape is what every such surface is expected to answer to, and it was the
  // one dismissal this drawer did not offer — a keyboard user could open it but had to Tab to a row
  // or reach for the backdrop to get out.
  //
  // ARMED ONLY WHILE IT IS OPEN, so no listener exists on the ordinary desktop layout, and no other
  // Escape handler on the page is shadowed. Bound to `document` rather than the aside, because focus
  // may legitimately sit on the header's menu button when the reader presses it.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div className="admin-shell">
      {/* R&S Part 1 §54 — keyboard users reach the page without tabbing through every sidebar row. */}
      <a
        className="skip-link"
        href="#main-content"
        onClick={(e) => {
          // Focus without the hash jump: following the anchor scrolled the whole app shell and left a
          // blank band above the header (browser-found, R&S Part 1). The URL also stays clean.
          e.preventDefault();
          document.getElementById("main-content")?.focus({ preventScroll: true });
        }}
      >
        Skip to main content
      </a>
      <Sidebar open={open} onNavigate={() => setOpen(false)} />
      {open && <div className="sidebar-backdrop d-lg-none" onClick={() => setOpen(false)} />}
      <div className="admin-main">
        <Header onToggleSidebar={toggle} sidebarOpen={open} />
        <main className="admin-content" id="main-content" tabIndex={-1}>
          {/* A page that throws is contained here (H-4): the sidebar and header stay mounted, so the
              user navigates away instead of reloading. Keyed by pathname, so that navigation also
              clears the error automatically. */}
          <RouteErrorBoundary>
            <Outlet />
          </RouteErrorBoundary>
        </main>
        <Footer />
      </div>
    </div>
  );
}
